import { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import type { SVGStep } from '../../api/xiaohua';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

interface XiaoHuaViewerProps {
  steps: SVGStep[];
  finalAnswer: string;
  summaryMarkdown: string;
  problemStem: string;
}

export function XiaoHuaViewer({ steps, finalAnswer, summaryMarkdown, problemStem }: XiaoHuaViewerProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const totalSteps = steps.length;
  const step = steps[currentStep];

  useEffect(() => {
    setCurrentStep(0);
    setShowAnswer(false);
  }, [steps]);

  // Clean up speech on unmount or step change
  useEffect(() => {
    return () => {
      if (utteranceRef.current) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
    };
  }, [currentStep]);

  const goNext = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowAnswer(true);
    }
  };

  const goPrev = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    if (showAnswer) {
      setShowAnswer(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const restart = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setCurrentStep(0);
    setShowAnswer(false);
  };

  const speak = () => {
    if (!step) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(step.speak_text);
    utter.lang = 'zh-CN';
    utter.rate = 0.9;
    utter.pitch = 1.1;
    utter.onend = () => setIsSpeaking(false);
    utter.onerror = () => setIsSpeaking(false);
    utteranceRef.current = utter;
    setIsSpeaking(true);
    window.speechSynthesis.speak(utter);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Problem header */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 mb-4">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg"> </span>
            <span className="text-sm font-bold text-amber-700">小画老师</span>
          </div>
          <p className="text-sm text-slate-700 font-medium">{problemStem}</p>
        </CardContent>
      </Card>

      {/* Main SVG area */}
      <div className="flex-1 flex flex-col items-center overflow-auto">
        {!showAnswer && step ? (
          <>
            {/* Step title */}
            <div className="w-full flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 text-white flex items-center justify-center font-bold text-sm shadow">
                {currentStep + 1}
              </div>
              <h3 className="text-lg font-bold text-slate-800">{step.title}</h3>
              <div className="ml-auto flex items-center gap-1 text-xs text-slate-400">
                {currentStep + 1} / {totalSteps}
              </div>
            </div>

            {/* SVG canvas */}
            <div
              ref={svgContainerRef}
              className="w-full max-w-[600px] bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden"
              dangerouslySetInnerHTML={{ __html: step.svg }}
            />

            {/* Explanation text */}
            <Card className="w-full max-w-[600px] mt-4 border-amber-100">
              <CardContent className="py-3 px-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 text-sm text-slate-700 leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        p: ({ node, ...props }) => <span {...props} />,
                      }}
                    >
                      {step.display_markdown}
                    </ReactMarkdown>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 border-amber-200 text-amber-700 hover:bg-amber-50"
                    onClick={isSpeaking ? stopSpeaking : speak}
                  >
                    {isSpeaking ? '⏸ 停止' : ' '}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        ) : showAnswer ? (
          <>
            {/* Final answer */}
            <Card className="w-full max-w-[600px] bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="py-6 px-6 text-center">
                <div className="text-3xl mb-3"> </div>
                <p className="text-xl font-bold text-green-800">{finalAnswer}</p>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="w-full max-w-[600px] mt-4">
              <CardContent className="py-4 px-4">
                <div className="prose prose-sm prose-slate max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {summaryMarkdown}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={goPrev}
          disabled={currentStep === 0 && !showAnswer}
          className="flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" />
          上一步
        </Button>

        <Button variant="ghost" size="icon" onClick={restart} title="重新开始">
          <RotateCcw className="w-4 h-4" />
        </Button>

        <Button
          onClick={goNext}
          disabled={showAnswer}
          className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
        >
          {currentStep === totalSteps - 1 ? '看答案' : '下一步'}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
